// SIP platform configuration for the platform-aware Create Trunk form.
// Pure module (no react/query imports) so every helper is directly unit-testable.
// Stored URIs are host-only: trunk-central rejects 'sip:' and '@' — the 'sip:'
// prefix shown in the design is a visual adornment, never part of the value.

import { REGIONS } from 'v2/configs/constants'

export type SipPlatformKey =
  | 'livekit'
  | 'retell'
  | 'vapi'
  | 'elevenlabs'
  | 'xai'
  | 'openai'
  | 'custom'
export type SipTransport = 'tcp' | 'udp' | 'tls'

export type SipUriMode = 'templated' | 'static' | 'freeform'
export type OutboundAclMode = 'managed' | 'custom' | 'unavailable'

export interface SipPlatformConfig {
  key: SipPlatformKey
  label: string
  /** Root domain used to recognize pasted URLs belonging to another platform */
  domain: string | null
  uriMode: SipUriMode
  /** templated mode: fixed host suffix appended to the editable subdomain */
  uriSuffix?: string
  uriPlaceholder?: string
  transports?: SipTransport[]
  defaultTransport?: SipTransport
  /** Port per transport, appended as :port (only platforms that require it) */
  ports?: Partial<Record<SipTransport, number>>
  outboundAcl: OutboundAclMode
  managedAclName?: string
  managedAclIps?: string[]
  /**
   * Platform documents no outbound calling at all (xAI: inbound + refer
   * transfers only), so the Create Trunk direction offers Inbound alone.
   */
  inboundOnly?: boolean
  /**
   * Platform routes by a fixed identifier in the INVITE user part instead of
   * the dialed number (OpenAI: project id). Stored on the URI as zentrunk's
   * `sip_user`; the dialed number moves to the SIP To header.
   */
  sipUser?: {
    label: string
    placeholder: string
    helper: string
  }
}

/**
 * Zentrunk's allowed sip_user charset (SIP-URI-safe subset). The FE MUST
 * enforce this itself: the public zentrunk API tolerates-and-DROPS invalid
 * values (2xx, value silently unstored), so without this check a bad project
 * id "saves" with no error anywhere and OpenAI later rejects every call.
 */
export const SIP_USER_PATTERN = /^[A-Za-z0-9._+-]+$/
export const SIP_USER_INVALID_MESSAGE = 'Only letters, numbers, . _ - + are allowed'

/**
 * Cleans a pasted value for the sip_user input: accepts OpenAI's full URI
 * (sip:proj_x@sip.api.openai.com;transport=tls) and keeps just the user part,
 * same paste-forgiveness as the LiveKit subdomain input.
 */
export const normalizeSipUserInput = (raw: string): string => {
  let value = raw.trim().replace(/^sips?:/i, '')
  const atIdx = value.indexOf('@')
  if (atIdx >= 0) value = value.slice(0, atIdx)
  value = value.split(';')[0]
  return value
}

const ELEVENLABS_GLOBAL_HOST = 'sip.rtc.elevenlabs.io'
const ELEVENLABS_INDIA_HOST = 'sip.rtc.in.residency.elevenlabs.io'

/** ElevenLabs SIP host follows the Plivo account's data region (IN → India residency) */
export const getElevenLabsHost = (region?: string): string =>
  region === REGIONS.IN ? ELEVENLABS_INDIA_HOST : ELEVENLABS_GLOBAL_HOST

export const SIP_PLATFORMS: Record<SipPlatformKey, SipPlatformConfig> = {
  livekit: {
    key: 'livekit',
    label: 'LiveKit Cloud',
    domain: 'livekit.cloud',
    uriMode: 'templated',
    uriSuffix: '.sip.livekit.cloud',
    uriPlaceholder: '{subdomain}',
    transports: ['tcp', 'tls'],
    defaultTransport: 'tcp',
    outboundAcl: 'unavailable',
  },
  retell: {
    key: 'retell',
    label: 'Retell AI',
    domain: 'retellai.com',
    uriMode: 'static',
    transports: ['tcp', 'udp', 'tls'],
    defaultTransport: 'tcp',
    outboundAcl: 'managed',
    managedAclName: 'retell-ai-acl',
    managedAclIps: [
      '18.98.16.120/30',
      '3.42.144.0/23',
      '153.57.128.0/18',
      '143.223.88.0/21',
      '161.115.160.0/19',
    ],
  },
  vapi: {
    key: 'vapi',
    label: 'Vapi',
    domain: 'vapi.ai',
    uriMode: 'static',
    transports: ['udp', 'tls'],
    defaultTransport: 'udp',
    outboundAcl: 'managed',
    managedAclName: 'vapi-acl',
    managedAclIps: ['44.229.228.186/32', '44.238.177.138/32', '63.182.83.170/32'],
  },
  elevenlabs: {
    key: 'elevenlabs',
    label: 'ElevenLabs Agents',
    domain: 'elevenlabs.io',
    uriMode: 'static',
    // UDP is experimental on ElevenLabs — dropped per design review (Tejal, Jul 2026)
    transports: ['tcp', 'tls'],
    defaultTransport: 'tcp',
    ports: { tcp: 5060, tls: 5061 },
    // Credentials, not a Plivo-managed ACL. ElevenLabs does not publish the source
    // IPs of its SIP traffic: their SIP trunking doc describes only "a /24 IP address
    // block containing 256 addresses distributed across multiple regions", disclosed
    // to enterprise static-IP customers and to nobody else. So there is no list we
    // can ship. We previously shipped the six addresses from their API allowlisting
    // page, which are where their WEBHOOKS and WebSocket calls egress from -- a
    // different set entirely -- so the managed ACL never authorised a customer's
    // INVITEs, and being read-only it could not be corrected either (customer report,
    // 2026-08-06). Plivo's own ElevenLabs integration guide uses credentials.
    //
    // 'custom' rather than 'unavailable' on purpose: an enterprise static-IP customer
    // IS issued a real range, and should be able to enter it. Retell and Vapi keep
    // managed ACLs because both publish stable signalling ranges (verified against
    // their docs).
    outboundAcl: 'custom',
  },
  xai: {
    key: 'xai',
    // Label straight from Tejal's design export (Aug 2026)
    label: 'xAI (SpaceXAI)',
    // 'voice.x.ai', not 'x.ai': foreign-platform detection substring-matches the
    // domain, and bare 'x.ai' sits inside unrelated hosts ('mypbx.ai')
    domain: 'voice.x.ai',
    uriMode: 'static',
    // TLS only, no port — the exact URI shape verified against xAI (Tejal,
    // 2026-08-07): sip.voice.x.ai;transport=tls. xAI routes on the dialed
    // number that Zentrunk auto-fills into the INVITE user part.
    transports: ['tls'],
    defaultTransport: 'tls',
    // xAI documents no outbound calling (only refer transfers of incoming
    // callers) and publishes no signaling IPs, so there is nothing to ACL.
    outboundAcl: 'unavailable',
    inboundOnly: true,
  },
  openai: {
    key: 'openai',
    label: 'OpenAI Realtime',
    // 'api.openai.com', not bare 'openai.com': foreign-platform detection
    // substring-matches the domain (same reasoning as xAI's 'voice.x.ai')
    domain: 'api.openai.com',
    uriMode: 'static',
    // TLS only per OpenAI's SIP docs; no port (matches the sip_user worked
    // example in the zentrunk doc: sip.api.openai.com;transport=tls)
    transports: ['tls'],
    defaultTransport: 'tls',
    // OpenAI supports no outbound trunks and publishes media-only CIDRs
    outboundAcl: 'unavailable',
    inboundOnly: true,
    // OpenAI routes by project id in the user part; host-only got 400
    // no_project_id (verified 2026-08-07). Requires zentrunk sip_user.
    sipUser: {
      label: 'Project ID',
      placeholder: 'proj_...',
      helper: 'Your OpenAI project ID (OpenAI dashboard → Settings → General).',
    },
  },
  custom: {
    key: 'custom',
    label: 'Custom SIP Platform',
    domain: null,
    uriMode: 'freeform',
    outboundAcl: 'custom',
  },
}

/** Dropdown order; custom is rendered last with the enterprise lock treatment */
export const SIP_PLATFORM_OPTIONS: SipPlatformKey[] = [
  'livekit',
  'retell',
  'vapi',
  'elevenlabs',
  'xai',
  'openai',
  'custom',
]

/**
 * Platforms with no India SIP endpoint. A trunk pointed at one of them from an
 * ap-south-1 account fails domestic-anchored, so offering it there is misleading
 * rather than merely useless (Sagar 2026-08-14, confirmed by Ayush in the thread).
 *
 * ElevenLabs is deliberately absent: it publishes an India residency host and
 * `getElevenLabsHost` already switches to it for IN accounts. LiveKit Cloud is
 * absent because the customer supplies their own subdomain. What lands here is a
 * platform pinned to a single global host.
 *
 * xAI qualifies on the same test and was requested separately, in review on the
 * xAI PR (#3642): its docs give one host, `sip.voice.x.ai`, for every provider
 * integration, with no regional SIP endpoint and no residency statement.
 */
// openai is here for two reasons: no India endpoint (US + EU data-residency
// hosts only) AND zentrunk's sip_user is deployed in US regions only — a stored
// value is a silent no-op elsewhere, so offering the platform would create
// trunks whose project id never reaches the wire.
export const PLATFORMS_WITHOUT_INDIA_ENDPOINT: SipPlatformKey[] = [
  'retell',
  'vapi',
  'xai',
  'openai',
]

export const isIndiaRegion = (region?: string): boolean => region === REGIONS.IN

/**
 * Whether Custom SIP Platform renders locked. India bypasses THIS gate only: a
 * self-hosted LiveKit or any other in-region stack has the customer's own domain,
 * and Custom SIP is the only option that can express it. US stays Enterprise-only
 * (product call, 2026-08-14).
 *
 * The region must not be folded into `isEnterpriseLocked`, which also gates
 * Messaging, geo permissions and Sender IDs. None of those are opening for India.
 */
export const isCustomSipLocked = (args: {
  isEnterpriseLocked: boolean
  customSipPlatformEnabled: boolean
  region?: string
}): boolean =>
  args.isEnterpriseLocked && !args.customSipPlatformEnabled && !isIndiaRegion(args.region)

/**
 * Platform options offered for the account's region. `selected` is always kept,
 * however unavailable: an existing Retell/Vapi/xAI trunk on an IN account must
 * still show its own platform when editing, or the select falls back to Custom and
 * offers it an auth type its platform refuses (failure mode 25).
 */
export const getRegionPlatformOptions = (
  region?: string,
  selected?: SipPlatformKey,
): SipPlatformKey[] => {
  if (!isIndiaRegion(region)) return SIP_PLATFORM_OPTIONS
  return SIP_PLATFORM_OPTIONS.filter(
    (key) => !PLATFORMS_WITHOUT_INDIA_ENDPOINT.includes(key) || key === selected,
  )
}

/** Inbound-only platforms hide the Outbound direction on the Create Trunk form */
export const isInboundOnlyPlatform = (key: SipPlatformKey | undefined): boolean =>
  !!key && !!SIP_PLATFORMS[key].inboundOnly

const getStaticHost = (key: SipPlatformKey, region?: string): string => {
  switch (key) {
    case 'retell':
      return 'sip.retellai.com'
    case 'vapi':
      return 'sip.vapi.ai'
    case 'xai':
      return 'sip.voice.x.ai'
    case 'openai':
      // US-only at launch (Plivo regions are US/IN; sip-eu.api.openai.com is
      // OpenAI data-residency, deferred). detectPlatformFromUri deliberately
      // does NOT match the EU host, so an EU URI falls through to 'custom' and
      // is edited as a raw URI rather than silently rebuilt onto this host.
      return 'sip.api.openai.com'
    case 'elevenlabs':
      return getElevenLabsHost(region)
    default:
      return ''
  }
}

/** Full stored URI for a fixed-host platform, e.g. sip.rtc.elevenlabs.io:5061;transport=tls */
export const buildStaticSipUri = (
  key: SipPlatformKey,
  transport?: SipTransport,
  region?: string,
): string => {
  const platform = SIP_PLATFORMS[key]
  const host = getStaticHost(key, region)
  if (!host || platform.uriMode !== 'static') return ''
  const t = transport ?? platform.defaultTransport ?? 'tcp'
  const port = platform.ports?.[t]
  return `${host}${port ? `:${port}` : ''};transport=${t}`
}

/** Full stored URI for a templated platform (LiveKit), from the user-entered subdomain */
export const buildTemplatedSipUri = (
  key: SipPlatformKey,
  subdomain: string,
  transport?: SipTransport,
): string => {
  const platform = SIP_PLATFORMS[key]
  if (platform.uriMode !== 'templated' || !subdomain) return ''
  const t = transport ?? platform.defaultTransport ?? 'tcp'
  return `${subdomain}${platform.uriSuffix};transport=${t}`
}

/** Host part of a stored URI: strips scheme, ;params, :port and user@ if present */
const extractHost = (uri: string): string => {
  let host = uri.trim().toLowerCase()
  host = host.replace(/^(sips?:|wss:\/\/|https?:\/\/)/, '')
  const atIdx = host.indexOf('@')
  if (atIdx >= 0) host = host.slice(atIdx + 1)
  host = host.split(';')[0]
  host = host.split('/')[0]
  host = host.split(':')[0]
  return host
}

const OPENAI_EU_HOST = 'sip-eu.api.openai.com'

/** Which platform an existing URI belongs to (drives dropdown filtering + edit inference) */
export const detectPlatformFromUri = (uri: string): SipPlatformKey | null => {
  const host = extractHost(uri)
  if (!host) return null
  if (host.endsWith('.sip.livekit.cloud')) return 'livekit'
  if (host === 'sip.retellai.com') return 'retell'
  if (host === 'sip.vapi.ai') return 'vapi'
  if (host === 'sip.voice.x.ai') return 'xai'
  // OPENAI_EU_HOST excluded: buildStaticSipUri only emits the US host, so
  // matching it would let an edit silently rewrite an EU URI onto it.
  if (host === 'sip.api.openai.com') return 'openai'
  if (host === ELEVENLABS_GLOBAL_HOST || host === ELEVENLABS_INDIA_HOST) return 'elevenlabs'
  return null
}

/** Broader than detectPlatformFromUri: the EU host is excluded there for edit
 * safety, but still needs its sip_user fetched for the dropdown label. */
export const hostNeedsSipUserFetch = (uri: string): boolean => {
  if (extractHost(uri) === OPENAI_EU_HOST) return true
  const platform = detectPlatformFromUri(uri)
  return !!platform && !!SIP_PLATFORMS[platform].sipUser
}

/**
 * Edit inference for OUTBOUND trunks, which carry neither a platform attribute
 * nor a URI to read it from — the only durable signal is the Plivo-managed IP
 * ACL attached at creation, whose name is fixed per platform (`vapi-acl`, and
 * `vapi-acl-2` when a name collision forced a suffixed copy).
 *
 * LiveKit and a credentials-only custom trunk are indistinguishable from stored
 * data, so both read back as custom and the user re-picks the platform in edit.
 * Persisting the platform on the trunk is the only real fix (backend field).
 */
export const detectPlatformFromAclName = (name?: string): SipPlatformKey | null => {
  if (!name) return null
  const normalized = name.trim().toLowerCase()
  for (const key of SIP_PLATFORM_OPTIONS) {
    const managed = SIP_PLATFORMS[key].managedAclName
    if (!managed) continue
    if (normalized === managed) return key
    // suffixed copy: resolveManagedAcl appends -2, -3, … on name collisions
    const suffix = normalized.slice(managed.length)
    if (normalized.startsWith(`${managed}-`) && /^-\d+$/.test(suffix)) return key
  }
  return null
}

/**
 * Reads an existing outbound trunk's auth back into the checkbox card's value.
 * Without this the card opens with nothing checked and the required check
 * blocks the update with "select at least one authentication type" — on a trunk
 * the customer never changed.
 */
export const deriveOutboundAuthType = (
  ipaclUuid?: string | null,
  credentialUuid?: string | null,
): 'ip_access_control_list' | 'credentials' | 'both' | undefined => {
  if (ipaclUuid && credentialUuid) return 'both'
  if (ipaclUuid) return 'ip_access_control_list'
  if (credentialUuid) return 'credentials'
  return undefined
}

/**
 * Display string for a stored URI: prefixes the fixed SIP user when one is
 * recorded (proj_x@sip.api.openai.com), so two OpenAI URIs are tellable apart
 * in the trunk form's dropdowns.
 */
export const displayOriginationUri = (uri: { uri: string; sip_user?: string }): string =>
  uri.sip_user ? `${uri.sip_user}@${uri.uri}` : uri.uri

/** URI-dropdown filter: custom shows everything, platforms show only their own URIs */
export const matchesPlatformUri = (uri: string, key: SipPlatformKey): boolean =>
  key === 'custom' ? true : detectPlatformFromUri(uri) === key

export interface NormalizedUriInput {
  value: string
  trimmed: boolean
}

/**
 * Cleans a pasted value for the templated subdomain input: strips schemes,
 * the platform's own suffix, params and trailing slashes — keeps the subdomain.
 */
export const normalizeUriInput = (raw: string, key: SipPlatformKey): NormalizedUriInput => {
  const platform = SIP_PLATFORMS[key]
  const original = raw.trim()
  let value = original.replace(/^(sips?:|wss:\/\/|https?:\/\/)/i, '')
  value = value.split(';')[0]
  if (platform.uriSuffix) {
    const idx = value.toLowerCase().indexOf(platform.uriSuffix.toLowerCase())
    if (idx >= 0) value = value.slice(0, idx)
  }
  value = value.replace(/\/+$/, '')
  return { value, trimmed: value !== original && original.length > 0 }
}

/** Detects a paste that belongs to a different platform (returns that platform) */
export const detectForeignPlatform = (
  raw: string,
  key: SipPlatformKey,
): SipPlatformConfig | null => {
  const lower = raw.toLowerCase()
  const own = SIP_PLATFORMS[key].domain
  for (const option of SIP_PLATFORM_OPTIONS) {
    const candidate = SIP_PLATFORMS[option]
    if (!candidate.domain || candidate.domain === own) continue
    if (lower.includes(candidate.domain)) return candidate
  }
  return null
}

export const uriMismatchMessage = (foreignLabel: string, ownLabel: string): string =>
  `This looks like a ${foreignLabel} URI. Enter a URI for ${ownLabel}.`

/** ACL API expects CIDR notation — bare IPs become /32 */
export const normalizeAclIp = (ip: string): string => (ip.includes('/') ? ip : `${ip}/32`)

/**
 * IPs the Plivo-managed ACL must contain for a platform. No region parameter: the
 * only region-aware entry was ElevenLabs' India-residency pair, and ElevenLabs no
 * longer has a managed ACL at all because it does not publish its SIP source IPs.
 * Retell and Vapi publish one range each, the same for every region.
 *
 * Do NOT reintroduce a region branch here without the platform's own SIP
 * documentation naming the addresses. The last attempt used their webhook egress
 * IPs, which authorise nothing.
 */
export const getManagedAclIps = (key: SipPlatformKey): string[] =>
  (SIP_PLATFORMS[key].managedAclIps ?? []).map(normalizeAclIp)

/** Platforms whose outbound trunks authenticate via a Plivo-managed IP ACL */
export const isManagedAclPlatform = (key: SipPlatformKey | undefined): boolean =>
  !!key && SIP_PLATFORMS[key].outboundAcl === 'managed'

/**
 * Default outbound auth for a platform, derived from its ACL mode: managed ACL →
 * ACL pre-selected (force-checked per design); everything else starts unselected
 * and the user picks (LiveKit's only pickable option is credentials, but it is a
 * deliberate click, not an auto-set).
 */
export const defaultOutboundAuthType = (
  key: SipPlatformKey | undefined,
): 'ip_access_control_list' | undefined => {
  if (!key) return undefined
  return SIP_PLATFORMS[key].outboundAcl === 'managed' ? 'ip_access_control_list' : undefined
}

/** Transport param of a stored URI, e.g. 'host;transport=tls' → 'tls' */
export const extractTransport = (uri: string): SipTransport | null => {
  const match = uri.toLowerCase().match(/;transport=(tcp|udp|tls)/)
  return match ? (match[1] as SipTransport) : null
}
